import { useState } from "react";
import { motion } from "motion/react";
import data from "../../data/index.json";
import "./Skilldrop.css";

const headingVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
};

const viewportConfig = { once: true, amount: 0.15 };

export default function Skilldrop() {
  const project = data?.skilldrop;
  const [copied, setCopied] = useState(false);

  const copyInstall = async () => {
    try {
      await navigator.clipboard.writeText(project.install);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Clipboard is unavailable (older browser or insecure context) — the
      // command stays visible and selectable, so nothing else to do here.
    }
  };

  if (!project) return null;

  return (
    <section className="skilldrop--section" id="OpenSource">
      <motion.div
        className="skilldrop--panel"
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={viewportConfig}
      >
        <div className="skilldrop--glow" aria-hidden="true" />

        <motion.div className="skilldrop--header" variants={headingVariants}>
          <span className="skilldrop--eyebrow">Open Source · Building in public</span>
          <h2 className="skilldrop--title">{project.name}</h2>
          <p className="skilldrop--tagline">{project.tagline}</p>
        </motion.div>

        <motion.div className="skilldrop--stats" variants={containerVariants}>
          {project.stats.map((stat) => (
            <motion.div className="skilldrop--stat" key={stat.label} variants={itemVariants}>
              <span className="skilldrop--stat--value">{stat.value}</span>
              <span className="skilldrop--stat--label">{stat.label}</span>
            </motion.div>
          ))}
        </motion.div>

        <motion.div className="skilldrop--pipeline" variants={itemVariants}>
          <div className="skilldrop--pipeline--steps">
            {project.pipeline.steps.map((step, index) => (
              <div className="skilldrop--step--group" key={step.name}>
                <span className="skilldrop--step">
                  {step.name}
                  <span className="skilldrop--step--gate">{step.gate}</span>
                </span>
                {index < project.pipeline.steps.length - 1 && (
                  <span className="skilldrop--step--arrow" aria-hidden="true">
                    →
                  </span>
                )}
              </div>
            ))}
          </div>
          <p className="skilldrop--pipeline--caption">{project.pipeline.caption}</p>
          {project.pipeline.link && (
            <a
              className="skilldrop--pipeline--link"
              href={project.pipeline.link.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {project.pipeline.link.label} →
            </a>
          )}
        </motion.div>

        <div className="skilldrop--body">
          <motion.div className="skilldrop--column" variants={itemVariants}>
            <h3 className="skilldrop--column--title">What I'm building</h3>
            <ul className="skilldrop--list">
              {project.building.map((point) => (
                <li key={point.title}>
                  <strong>{point.title}</strong> {point.detail}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div className="skilldrop--column" variants={itemVariants}>
            <h3 className="skilldrop--column--title">Role-based packs</h3>
            <ul className="skilldrop--packs">
              {project.packs.map((pack) => (
                <li className="skilldrop--pack" key={pack.name}>
                  <span className="skilldrop--pack--name">{pack.name}</span>
                  <span className="skilldrop--pack--count">{pack.count}</span>
                </li>
              ))}
            </ul>
            {project.packsMore && <p className="skilldrop--packs--more">{project.packsMore}</p>}

            <div className="skilldrop--terminal">
              <div className="skilldrop--terminal--bar">
                <span className="skilldrop--dot" />
                <span className="skilldrop--dot" />
                <span className="skilldrop--dot" />
                <span className="skilldrop--terminal--label">install</span>
              </div>
              <div className="skilldrop--terminal--body">
                <code>
                  <span className="skilldrop--prompt">$</span> {project.install}
                </code>
                <button
                  type="button"
                  className="skilldrop--copy"
                  onClick={copyInstall}
                  aria-label="Copy install command"
                >
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div className="skilldrop--tags" variants={itemVariants}>
          {project.worksWith.map((tool) => (
            <span className="skilldrop--tag" key={tool}>
              {tool}
            </span>
          ))}
        </motion.div>

        <motion.div className="skilldrop--actions" variants={itemVariants}>
          <motion.a
            className="btn skilldrop--btn--primary"
            href={project.repo}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.98 }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.438 9.8 8.205 11.387.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61-.546-1.387-1.332-1.756-1.332-1.756-1.09-.745.083-.73.083-.73 1.205.085 1.838 1.238 1.838 1.238 1.07 1.834 2.807 1.305 3.49.997.108-.777.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.124-.302-.54-1.523.117-3.176 0 0 1.005-.322 3.3 1.23a11.57 11.57 0 0 1 3-.39c1.016.003 2.038.136 3 .39 2.292-1.553 3.297-1.23 3.297-1.23.662 1.653.246 2.874.122 3.176.77.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.43.372.815 1.102.815 2.22 0 1.605-.015 2.896-.015 3.286 0 .32.21.695.825.576C20.565 21.793 24 17.3 24 12c0-6.63-5.37-12-12-12z" />
            </svg>
            View on GitHub
          </motion.a>
          <motion.a
            className="btn skilldrop--btn--ghost"
            href={project.catalog}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.98 }}
          >
            Browse the skill catalog
          </motion.a>
        </motion.div>
      </motion.div>
    </section>
  );
}
